import type { ReactNode } from "react";
import Drawer from "@mui/material/Drawer";
import { PButton } from "./PButton";
import { PrimeGlyph } from "./PrimeGlyph";

// PRIMENG `p-sidebar` AS THE PILLARS THEME DRAWS IT: HEADER 1REM 1.25REM WITH A 28PX
// ROUND CLOSE BUTTON PUSHED RIGHT; CONTENT 1.25REM, WITH NO TOP PADDING UNDER THE
// HEADER; 1PX `rgba(0,0,0,.2)` BORDER AND THE PILLARS SOFT SHADOW. `width` IS THE
// TEMPLATE'S RESPONSIVE WIDTH (E.G. `w-full md:w-30rem` = { xs: "100%", md: "30rem" }).
// NON-MODAL SIDEBARS HAVE NO MASK AND DO NOT CLOSE ON AN OUTSIDE CLICK.
export function PSidebar(props: {
  open: boolean;
  onClose: () => void;
  position?: "left" | "right";
  modal?: boolean;
  // `[dismissible]="false"`: A MODAL SIDEBAR WHOSE MASK DOES NOT CLOSE IT
  dismissible?: boolean;
  // `[closeOnEscape]="false"`
  closeOnEscape?: boolean;
  width: Record<string, string> | string;
  header?: ReactNode;
  // `styleClass="no-padding"`: THE CONTENT LOSES ITS 1.25REM PADDING (`_overrides.scss`)
  noPadding?: boolean;
  // A `p-sidebar-footer`: 1REM 1.25REM BELOW THE CONTENT
  footer?: ReactNode;
  ariaLabel?: string;
  children: ReactNode;
}) {
  const { open, onClose, position = "left", modal = true, dismissible = true, closeOnEscape = true, width, header, noPadding = false, footer, ariaLabel, children } = props;
  return (
    <Drawer
      anchor={position}
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" && !dismissible) return;
        if (reason === "escapeKeyDown" && !closeOnEscape) return;
        onClose();
      }}
      hideBackdrop={!modal}
      // A NON-MODAL SIDEBAR LEAVES THE PAGE BEHIND IT USABLE
      disableScrollLock={!modal}
      disableEnforceFocus={!modal}
      sx={!modal ? { pointerEvents: "none", "& .MuiDrawer-paper": { pointerEvents: "auto" } } : undefined}
      slotProps={{
        backdrop: { sx: { backgroundColor: "var(--maskbg)" } },
        paper: {
          "aria-label": ariaLabel,
          sx: {
            width,
            maxWidth: "100vw",
            border: "1px solid rgba(0, 0, 0, 0.2)",
            borderRadius: 0,
            boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.02), 0px 0px 2px rgba(0, 0, 0, 0.05), 0px 1px 4px rgba(0, 0, 0, 0.08)",
          },
        },
      }}
    >
      <div className="flex align-items-center" style={{ padding: "1rem 1.25rem" }}>
        <div className="flex-grow-1">{header}</div>
        <PButton
          icon={<PrimeGlyph name="times" />}
          rounded
          variant="text"
          severity="secondary"
          ariaLabel="Close"
          onClick={onClose}
          sx={{ width: "2rem", height: "2rem", ml: "auto" }}
        />
      </div>
      <div style={{ padding: noPadding ? 0 : "0 1.25rem 1.25rem 1.25rem", flex: "1 1 auto", overflowY: "auto" }}>{children}</div>
      {footer && <div style={{ padding: "1rem 1.25rem" }}>{footer}</div>}
    </Drawer>
  );
}
